import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';

const OPTIONS = ['A', 'B', 'C', 'D', 'E'];

interface SubjectDef {
  key: string;
  label: string;
  shortLabel: string;
  questionCount: number;
}

interface YKSOptikFormProps {
  onSubmit?: (payload: {
    kitapcikTuru: 'A' | 'B' | null;
    answers: Record<string, (string | null)[]>;
    branchSelections?: Partial<Record<'sosyal_bilimler' | 'sosyal_bilimler2', 'din' | 'felsefe'>>;
  }) => void | Promise<void>;
  submitLabel?: string;
  submitting?: boolean;
  hideBookletSelector?: boolean;
  questionCountOverrides?: Record<string, number>;
  answerKey?: Record<string, any[]>;
  initialAnswers?: Record<string, Array<string | null>>;
  initialKitapcikTuru?: 'A' | 'B' | null;
  initialBranchSelections?: Partial<Record<'sosyal_bilimler' | 'sosyal_bilimler2', 'din' | 'felsefe'>>;
  availableSegments?: string[];
}

function getTitleSizeClass(label: string) {
  if (label.length <= 10) return 'lgs-card-title-short';
  if (label.length <= 18) return 'lgs-card-title-medium';
  return 'lgs-card-title-long';
}

const TYT_SUBJECTS: SubjectDef[] = [
  { key: 'turkce', label: 'Türkçe', shortLabel: 'TÜR', questionCount: 40 },
  { key: 'sosyal_bilimler', label: 'Sosyal Bilimler', shortLabel: 'SOS', questionCount: 20 },
  { key: 'temel_matematik', label: 'Temel Matematik', shortLabel: 'MAT', questionCount: 40 },
  { key: 'fen_bilimleri', label: 'Fen Bilimleri', shortLabel: 'FEN', questionCount: 20 },
];

const AYT_SUBJECTS: SubjectDef[] = [
  { key: 'edebiyat_sosyal1', label: 'Türk Dili ve Edebiyatı - Sosyal Bilimler-1', shortLabel: 'TDE-S1', questionCount: 40 },
  { key: 'sosyal_bilimler2', label: 'Sosyal Bilimler-2', shortLabel: 'SOS-2', questionCount: 40 },
  { key: 'matematik', label: 'Matematik', shortLabel: 'MAT', questionCount: 40 },
  { key: 'fen_bilimleri_ayt', label: 'Fen Bilimleri', shortLabel: 'FEN', questionCount: 40 },
];

const Bubble: React.FC<{
  label: string;
  filled: boolean;
  onClick: () => void;
}> = React.memo(({ label, filled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="lgs-bubble"
    data-filled={filled || undefined}
  >
    {label}
  </button>
));

const SubjectGrid: React.FC<{
  subject: SubjectDef;
  answers: (string | null)[];
  onSelect: (qIdx: number, option: string) => void;
}> = React.memo(({ subject, answers, onSelect }) => {
  return (
    <div className="lgs-card">
      <div className="lgs-card-head">
        <span className={`lgs-card-title ${getTitleSizeClass(subject.label)}`}>{subject.label}</span>
      </div>

      <div className="lgs-card-body">
        {Array.from({ length: subject.questionCount }).map((_, qi) => (
          <div key={qi} className="lgs-row">
            <span className="lgs-qno">{qi + 1}</span>
            {OPTIONS.map((opt) => (
              <Bubble key={opt} label={opt} filled={answers[qi] === opt} onClick={() => onSelect(qi, opt)} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
});

const SubjectDropdown: React.FC<{
  subjects: SubjectDef[];
  answers: Record<string, (string | null)[]>;
  selectedSubject: string | null;
  onSelect: (key: string) => void;
  open: boolean;
  onClose: () => void;
}> = ({ subjects, answers, selectedSubject, onSelect, open, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div ref={ref} className="lgs-dd">
      {subjects.map((sub) => {
        const filled = answers[sub.key]?.filter(Boolean).length || 0;
        return (
          <button
            key={sub.key}
            type="button"
            className={`lgs-dd-item ${selectedSubject === sub.key ? 'lgs-dd-item-on' : ''}`}
            onClick={() => { onSelect(sub.key); onClose(); }}
          >
            <div className="lgs-dd-item-left">
              <div className={`lgs-dd-dot ${filled === 0 ? '' : filled === sub.questionCount ? 'lgs-dd-dot-done' : 'lgs-dd-dot-part'}`} />
              <span>{sub.label}</span>
            </div>
            <span className="lgs-dd-badge">{filled}/{sub.questionCount}</span>
          </button>
        );
      })}
    </div>
  );
};

export default function YKSOptikForm({
  onSubmit,
  submitLabel = 'Bitir',
  submitting = false,
  hideBookletSelector = false,
  questionCountOverrides,
  answerKey,
  initialAnswers,
  initialKitapcikTuru,
  initialBranchSelections,
  availableSegments,
}: YKSOptikFormProps) {
  const normalizedSegments = useMemo(
    () => (Array.isArray(availableSegments) && availableSegments.length > 0 ? availableSegments : ['tyt', 'ayt']),
    [availableSegments]
  );
  const hasTYT = normalizedSegments.includes('tyt');
  const hasAYT = normalizedSegments.includes('ayt');
  const tytSubjects = useMemo(() => TYT_SUBJECTS.map((subject) => ({
    ...subject,
    questionCount: questionCountOverrides?.[subject.key] ?? subject.questionCount,
  })), [questionCountOverrides]);
  const aytSubjects = useMemo(() => AYT_SUBJECTS.map((subject) => ({
    ...subject,
    questionCount: questionCountOverrides?.[subject.key] ?? subject.questionCount,
  })), [questionCountOverrides]);
  const allSubjects = useMemo(
    () => [
      ...(hasTYT ? tytSubjects : []),
      ...(hasAYT ? aytSubjects : []),
    ],
    [aytSubjects, hasAYT, hasTYT, tytSubjects]
  );
  const [kitapcikTuru, setKitapcikTuru] = useState<'A' | 'B' | null>(hideBookletSelector ? 'A' : (initialKitapcikTuru || null));
  const [activeSection, setActiveSection] = useState<'tyt' | 'ayt'>(hasTYT ? 'tyt' : 'ayt');
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [ddOpen, setDdOpen] = useState<'tyt' | 'ayt' | null>(null);
  const [branchSelections, setBranchSelections] = useState<Partial<Record<'sosyal_bilimler' | 'sosyal_bilimler2', 'din' | 'felsefe'>>>({
    sosyal_bilimler: 'din',
    sosyal_bilimler2: 'din',
  });
  const [isShuffleModalOpen, setIsShuffleModalOpen] = useState(false);
  const [shuffleMode, setShuffleMode] = useState<'mixed' | 'single' | 'allCorrect' | 'everyFourthWrong'>('mixed');
  const [leaveBlankEveryFourth, setLeaveBlankEveryFourth] = useState(true);
  const [modalKitapcikTuru, setModalKitapcikTuru] = useState<'A' | 'B' | null>(hideBookletSelector ? 'A' : null);
  const [modalBranchSelections, setModalBranchSelections] = useState<Partial<Record<'sosyal_bilimler' | 'sosyal_bilimler2', 'din' | 'felsefe'>>>({
    sosyal_bilimler: 'din',
    sosyal_bilimler2: 'din',
  });

  useEffect(() => {
    if (activeSection === 'ayt' && !hasAYT && hasTYT) {
      setActiveSection('tyt');
      setSelectedSubject(null);
    }
    if (activeSection === 'tyt' && !hasTYT && hasAYT) {
      setActiveSection('ayt');
      setSelectedSubject(null);
    }
  }, [activeSection, hasAYT, hasTYT]);

  const [answers, setAnswers] = useState<Record<string, (string | null)[]>>(() => {
    const init: Record<string, (string | null)[]> = {};
    allSubjects.forEach((s) => {
      init[s.key] = Array(s.questionCount).fill(null);
    });
    return init;
  });

  useEffect(() => {
    setKitapcikTuru(hideBookletSelector ? 'A' : (initialKitapcikTuru || null));
  }, [hideBookletSelector, initialKitapcikTuru]);

  useEffect(() => {
    setBranchSelections({
      sosyal_bilimler: initialBranchSelections?.sosyal_bilimler === 'felsefe' ? 'felsefe' : 'din',
      sosyal_bilimler2: initialBranchSelections?.sosyal_bilimler2 === 'felsefe' ? 'felsefe' : 'din',
    });
  }, [initialBranchSelections]);

  useEffect(() => {
    const nextAnswers: Record<string, (string | null)[]> = {};
    allSubjects.forEach((subject) => {
      const seeded = Array.isArray(initialAnswers?.[subject.key]) ? initialAnswers?.[subject.key] || [] : [];
      nextAnswers[subject.key] = Array.from({ length: subject.questionCount }, (_, index) => {
        const value = seeded[index];
        return typeof value === 'string' && value.trim() ? value : null;
      });
    });
    setAnswers(nextAnswers);
  }, [allSubjects, initialAnswers]);

  const handleAnswer = useCallback((subKey: string, qi: number, opt: string) => {
    setAnswers((p) => {
      const arr = [...(p[subKey] || [])];
      arr[qi] = arr[qi] === opt ? null : opt;
      return { ...p, [subKey]: arr };
    });
  }, []);

  const handleSectionClick = useCallback((section: 'tyt' | 'ayt') => {
    if (activeSection === section) {
      setDdOpen(null);
      return;
    }
    setActiveSection(section);
    setSelectedSubject(null);
    setDdOpen(null);
  }, [activeSection]);

  const handleSubjectPick = useCallback((key: string) => {
    const isTYT = tytSubjects.some((s) => s.key === key);
    setActiveSection(isTYT ? 'tyt' : 'ayt');
    setSelectedSubject(key);
  }, [tytSubjects]);

  const currentSubjects = activeSection === 'tyt' ? tytSubjects : activeSection === 'ayt' ? aytSubjects : [];
  const subjectsToRender = selectedSubject ? currentSubjects.filter((s) => s.key === selectedSubject) : currentSubjects;

  const totalAll = allSubjects.reduce((s, sub) => s + sub.questionCount, 0);
  const filledAll = Object.values(answers).reduce((s, arr) => s + arr.filter(Boolean).length, 0);

  const getSectionProgress = (subjects: SubjectDef[]) => {
    const t = subjects.reduce((s, sub) => s + sub.questionCount, 0);
    const f = subjects.reduce((s, sub) => s + (answers[sub.key]?.filter(Boolean).length || 0), 0);
    return { total: t, filled: f };
  };

  const tytP = getSectionProgress(hasTYT ? tytSubjects : []);
  const aytP = getSectionProgress(hasAYT ? aytSubjects : []);
  const canSubmit = filledAll > 0 && (hideBookletSelector || Boolean(kitapcikTuru));

  const buildCorrectAnswerMap = useCallback((
    activeBranchSelections: Partial<Record<'sosyal_bilimler' | 'sosyal_bilimler2', 'din' | 'felsefe'>>,
    activeBookletType: 'A' | 'B' | null,
  ) => {
    const map: Record<string, string[]> = {};
    const sortedTestIds = Object.keys(answerKey || {}).sort((left, right) => Number(left) - Number(right));
    const normalizeLabel = (value: string) => (value || '').toLocaleLowerCase('tr-TR').replace(/ı/g, 'i');

    allSubjects.forEach((subject, index) => {
      const testId = sortedTestIds[index];
      let questions = testId ? [...(answerKey?.[testId] || [])] : [];
      if (subject.key === 'sosyal_bilimler' || subject.key === 'sosyal_bilimler2') {
        const selectedBranch = subject.key === 'sosyal_bilimler'
          ? (activeBranchSelections.sosyal_bilimler === 'felsefe' ? 'felsefe' : 'din')
          : (activeBranchSelections.sosyal_bilimler2 === 'felsefe' ? 'felsefe' : 'din');
        const commonEnd = subject.key === 'sosyal_bilimler' ? 15 : 34;
        const branchStart = commonEnd + 1;
        const branchEnd = subject.key === 'sosyal_bilimler' ? 20 : 40;

        questions = questions.filter((question) => {
          const questionNumber = Number(question?.aQuestionNumber || question?.absoluteQuestionNumber || 0);
          const subjectName = normalizeLabel(String(question?.subjectName || ''));
          if (questionNumber <= commonEnd) return true;
          if (questionNumber < branchStart || questionNumber > branchEnd) return false;
          if (selectedBranch === 'felsefe') return subjectName.includes('ilave felsefe');
          return subjectName.includes('din kültürü') || subjectName.includes('din kulturu');
        });
      }

      const orderedAnswers = Array(subject.questionCount).fill('');
      questions.forEach((question) => {
        const bookletOrder = activeBookletType === 'B'
          ? Number(question?.bQuestionNumber || question?.aQuestionNumber || question?.absoluteQuestionNumber || 0)
          : Number(question?.aQuestionNumber || question?.absoluteQuestionNumber || 0);

        if (!bookletOrder || bookletOrder < 1 || bookletOrder > subject.questionCount) return;
        orderedAnswers[bookletOrder - 1] = String(question?.correctAnswer || '');
      });

      map[subject.key] = orderedAnswers;
    });

    return map;
  }, [allSubjects, answerKey]);

  useEffect(() => {
    if (hideBookletSelector && kitapcikTuru !== 'A') {
      setKitapcikTuru('A');
    }
  }, [hideBookletSelector, kitapcikTuru]);

  useEffect(() => {
    if (hideBookletSelector && modalKitapcikTuru !== 'A') {
      setModalKitapcikTuru('A');
    }
  }, [hideBookletSelector, modalKitapcikTuru]);

  const handleFinish = useCallback(() => {
    if (!onSubmit || !canSubmit) return;
    onSubmit({
      kitapcikTuru: hideBookletSelector ? 'A' : kitapcikTuru,
      answers,
      branchSelections,
    });
  }, [answers, branchSelections, canSubmit, hideBookletSelector, kitapcikTuru, onSubmit]);

  const handleShuffleApply = useCallback(() => {
    const getWrongOption = (correctAnswer: string | null, index: number) => {
      const normalizedCorrect = correctAnswer && OPTIONS.includes(correctAnswer) ? correctAnswer : null;
      const fallbackIndex = normalizedCorrect
        ? (OPTIONS.indexOf(normalizedCorrect) + 1 + (index % Math.max(OPTIONS.length - 1, 1))) % OPTIONS.length
        : index % OPTIONS.length;
      const candidate = OPTIONS[fallbackIndex];
      return candidate !== normalizedCorrect ? candidate : OPTIONS[(fallbackIndex + 1) % OPTIONS.length];
    };

    const effectiveBranchSelections = {
      sosyal_bilimler: modalBranchSelections.sosyal_bilimler === 'felsefe' ? 'felsefe' : 'din',
      sosyal_bilimler2: modalBranchSelections.sosyal_bilimler2 === 'felsefe' ? 'felsefe' : 'din',
    } as const;
    const effectiveBookletType = hideBookletSelector ? 'A' : (modalKitapcikTuru || kitapcikTuru || 'A');
    const effectiveCorrectAnswerMap = buildCorrectAnswerMap(effectiveBranchSelections, effectiveBookletType);

    setKitapcikTuru(effectiveBookletType);
    setBranchSelections(effectiveBranchSelections);
    setAnswers(() => {
      const nextAnswers: Record<string, (string | null)[]> = {};
      allSubjects.forEach((subject, subjectIndex) => {
        const subjectOption = OPTIONS[subjectIndex % OPTIONS.length];
        const correctAnswers = effectiveCorrectAnswerMap[subject.key] || [];
        nextAnswers[subject.key] = Array.from({ length: subject.questionCount }, (_, index) => {
          const correctAnswer = correctAnswers[index] && OPTIONS.includes(correctAnswers[index]) ? correctAnswers[index] : null;
          if (shuffleMode === 'allCorrect') return correctAnswer;
          if (shuffleMode === 'everyFourthWrong') {
            if ((index + 1) % 4 === 0) return getWrongOption(correctAnswer, index);
            return correctAnswer;
          }
          if (leaveBlankEveryFourth && (index + 1) % 4 === 0) return null;
          if (shuffleMode === 'single') return subjectOption;
          return OPTIONS[Math.floor(Math.random() * OPTIONS.length)];
        });
      });
      return nextAnswers;
    });
    setIsShuffleModalOpen(false);
  }, [allSubjects, buildCorrectAnswerMap, hideBookletSelector, kitapcikTuru, leaveBlankEveryFourth, modalBranchSelections, modalKitapcikTuru, shuffleMode]);

  const handleOpenShuffleModal = useCallback(() => {
    setModalKitapcikTuru(hideBookletSelector ? 'A' : (kitapcikTuru || 'A'));
    setModalBranchSelections({
      sosyal_bilimler: branchSelections.sosyal_bilimler === 'felsefe' ? 'felsefe' : 'din',
      sosyal_bilimler2: branchSelections.sosyal_bilimler2 === 'felsefe' ? 'felsefe' : 'din',
    });
    setIsShuffleModalOpen(true);
  }, [branchSelections, hideBookletSelector, kitapcikTuru]);

  return (
    <div className="lgs-wrap">
      <style>{`
        .lgs-wrap {
          width: 100%;
          padding: 0;
        }
        .lgs-root {
          max-width: 1100px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
        }

        /* ─── Top Bar ─── */
        .lgs-topbar {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        /* ─── Section Buttons ─── */
        .lgs-sections {
          display: flex;
          gap: 10px;
          flex: 1;
        }

        .lgs-sec-wrap {
          position: relative;
          flex: 1;
        }

        .lgs-sec {
          display: flex;
          align-items: stretch;
          border-radius: 14px;
          overflow: hidden;
          background: #fff;
          border: 2px solid #e8e8ea;
          transition: all 0.2s;
          cursor: pointer;
        }

        .lgs-sec:hover {
          border-color: #d63384;
        }

        .lgs-sec[data-active] {
          border-color: #d63384;
          box-shadow: 0 4px 16px rgba(214,51,132,0.15);
        }

        .lgs-sec-main {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 14px 20px;
          border: none;
          background: transparent;
          cursor: pointer;
          transition: all 0.15s;
        }

        .lgs-sec[data-active] .lgs-sec-main {
          background: linear-gradient(135deg, #d63384, #b5246a);
        }

        .lgs-sec-label {
          font-size: 15px;
          font-weight: 700;
          color: #555;
          text-transform: uppercase;
          letter-spacing: 2px;
        }

        .lgs-sec[data-active] .lgs-sec-label {
          color: #fff;
        }

        .lgs-sec-prog {
          font-size: 11px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 8px;
          background: #f0f0f0;
          color: #999;
        }

        .lgs-sec[data-active] .lgs-sec-prog {
          background: rgba(255,255,255,0.2);
          color: rgba(255,255,255,0.9);
        }

        .lgs-sec-arrow {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 42px;
          border: none;
          border-left: 2px solid #e8e8ea;
          background: transparent;
          color: #ccc;
          cursor: pointer;
          transition: all 0.15s;
        }

        .lgs-sec-arrow:hover {
          background: #fafafa;
          color: #d63384;
        }

        .lgs-sec[data-active] .lgs-sec-arrow {
          border-left-color: rgba(255,255,255,0.2);
          background: linear-gradient(135deg, #c2185b, #a31455);
          color: #fff;
        }

        .lgs-sec[data-active] .lgs-sec-arrow:hover {
          background: linear-gradient(135deg, #b5246a, #8e1148);
          color: #fff;
        }

        /* ─── Kitapçık Türü ─── */
        .lgs-kt {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #fff;
          border: 2px solid #e8e8ea;
          border-radius: 14px;
          padding: 6px 8px;
          flex-shrink: 0;
        }

        .lgs-kt-label {
          font-size: 10px;
          font-weight: 700;
          color: #bbb;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 0 6px;
          white-space: nowrap;
        }

        .lgs-kt-btn {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          border: 2px solid #d63384;
          background: transparent;
          color: rgba(214,51,132,0.35);
          font-size: 16px;
          font-weight: 800;
          cursor: pointer;
          transition: all 0.15s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .lgs-kt-btn:hover {
          background: rgba(214,51,132,0.06);
          color: #d63384;
        }

        .lgs-kt-btn[data-on] {
          background: #d63384;
          color: #fff;
          box-shadow: 0 2px 8px rgba(214,51,132,0.3);
        }

        .lgs-branch {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
          padding: 12px 14px;
          border-radius: 14px;
          border: 1px solid #f0e6ec;
          background: linear-gradient(180deg, #fff8fb 0%, #fff 100%);
        }

        .lgs-branch-title {
          font-size: 13px;
          font-weight: 700;
          color: #7b8090;
        }

        .lgs-branch-actions {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px;
          border-radius: 12px;
          background: #fff;
          border: 1px solid #efe6ec;
        }

        .lgs-branch-btn {
          padding: 8px 12px;
          border: none;
          border-radius: 10px;
          background: transparent;
          color: #8d94a5;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
        }

        .lgs-branch-btn[data-on] {
          background: linear-gradient(135deg, #d63384, #b5246a);
          color: #fff;
          box-shadow: 0 6px 14px rgba(214,51,132,0.18);
        }

        /* ─── Subject Tabs ─── */
        .lgs-tabs {
          display: flex;
          gap: 0;
          margin-bottom: 14px;
          background: #fff;
          border-radius: 12px;
          border: 1.5px solid #e8e8ea;
          overflow: hidden;
        }

        .lgs-tab {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 11px 12px;
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
          color: #888;
          transition: all 0.15s;
          border-right: 1px solid #f0f0f0;
          position: relative;
        }

        .lgs-tab:last-child {
          border-right: none;
        }

        .lgs-tab:hover {
          background: #fafafa;
          color: #d63384;
        }

        .lgs-tab[data-on] {
          background: linear-gradient(135deg, rgba(214,51,132,0.08), rgba(214,51,132,0.03));
          color: #d63384;
          font-weight: 700;
        }

        .lgs-tab[data-on]::after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 10%;
          right: 10%;
          height: 2.5px;
          background: #d63384;
          border-radius: 2px 2px 0 0;
        }

        .lgs-tab-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #e0e0e0;
          flex-shrink: 0;
        }

        .lgs-tab-count {
          font-size: 11px;
          font-weight: 700;
          color: #b8b8b8;
          letter-spacing: 0;
        }

        .lgs-tab[data-on] .lgs-tab-count {
          color: #d63384;
        }

        .lgs-tab-dot[data-part] { background: #ffc107; }
        .lgs-tab-dot[data-done] { background: #4caf50; }

        /* ─── Dropdown ─── */
        .lgs-dd {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          background: #fff;
          border: 1.5px solid #e8e8ea;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.1);
          z-index: 50;
          overflow: hidden;
          animation: lgs-dd-in 0.12s ease-out;
        }

        @keyframes lgs-dd-in {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .lgs-dd-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 12px 16px;
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 13px;
          color: #555;
          transition: all 0.1s;
        }

        .lgs-dd-item:hover { background: #fafafa; color: #d63384; }
        .lgs-dd-item + .lgs-dd-item { border-top: 1px solid #f5f5f5; }
        .lgs-dd-item-on { background: rgba(214,51,132,0.05) !important; color: #d63384 !important; font-weight: 600; }

        .lgs-dd-item-left { display: flex; align-items: center; gap: 10px; }

        .lgs-dd-dot { width: 8px; height: 8px; border-radius: 50%; background: #e0e0e0; }
        .lgs-dd-dot-part { background: #ffc107; }
        .lgs-dd-dot-done { background: #4caf50; }

        .lgs-dd-badge { font-size: 11px; font-weight: 600; color: #bbb; background: #f5f5f5; padding: 2px 8px; border-radius: 6px; }
        .lgs-dd-item-on .lgs-dd-badge { background: rgba(214,51,132,0.1); color: #d63384; }

        /* ─── Subject Cards ─── */
        .lgs-grid {
          display: flex;
          gap: 10px;
          flex-wrap: nowrap;
          justify-content: center;
          align-items: flex-start;
        }

        .lgs-grid[data-count="4"] {
          justify-content: space-between;
        }

        .lgs-grid[data-count="2"] {
          gap: 22px;
        }

        .lgs-grid[data-count="1"] {
          justify-content: center;
        }

        @media (max-width: 768px) {
          .lgs-wrap {
            padding: 12px;
          }

          .lgs-root {
            min-height: calc(100vh - 24px);
          }

          .lgs-topbar {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          }

          .lgs-sections {
            width: 100%;
          }

          .lgs-kt {
            align-self: center;
          }

          .lgs-tabs {
            display: grid;
            grid-template-columns: repeat(6, minmax(0, 1fr));
            overflow: hidden;
          }

          .lgs-tab {
            flex: none;
            min-width: 0;
            padding: 10px 8px;
            white-space: nowrap;
            grid-column: span 2;
            border-bottom: 1px solid #f0f0f0;
          }

          .lgs-tab:nth-child(3n) {
            border-right: none;
          }

          .lgs-tab:nth-last-child(-n + 2) {
            border-bottom: none;
          }

          .lgs-tab:nth-child(4) {
            grid-column: 2 / span 2;
          }

          .lgs-tab:nth-child(5) {
            grid-column: 4 / span 2;
          }

          .lgs-grid {
            flex-direction: column;
            align-items: center;
            gap: 16px;
          }

          .lgs-card,
          .lgs-card-compact {
            flex: none;
            width: 100% !important;
            max-width: none !important;
          }

          .lgs-card-head {
            height: 80px;
            padding: 12px 16px;
          }

          .lgs-card-title {
            font-size: 14px;
            letter-spacing: 0.4px;
          }

          .lgs-card-title-short {
            font-size: 18px;
          }

          .lgs-card-title-medium {
            font-size: 16px;
          }

          .lgs-card-title-long {
            font-size: 14px;
          }

          .lgs-card-body {
            padding: 12px 14px 16px;
          }

          .lgs-row {
            gap: 4px;
          }

          .lgs-row + .lgs-row {
            margin-top: 4px;
          }

          .lgs-qno {
            width: 30px;
            font-size: 16px;
            padding-right: 4px;
          }

          .lgs-col-label {
            width: 42px;
            font-size: 15px;
          }

          .lgs-bubble {
            width: 42px;
            height: 42px;
            min-width: 42px;
            min-height: 42px;
            border-width: 2.5px;
            font-size: 16px;
          }

          .lgs-finish {
            padding: 12px 14px;
          }

          .lgs-finish-btn {
            padding: 10px 22px;
          }
        }

        .lgs-card {
          flex: 0 1 calc((100% - 30px) / 4);
          width: calc((100% - 30px) / 4);
          max-width: calc((100% - 30px) / 4);
          background: #fff;
          border-radius: 12px;
          border: 1.5px solid #e8e8ea;
          overflow: hidden;
          transition: box-shadow 0.2s;
        }

        .lgs-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.06); }

        .lgs-card-head {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 93px;
          padding: 15px 21px;
          box-sizing: border-box;
          background: #fff4f8;
          border-bottom: 1px solid #f0f0f0;
        }

        .lgs-card-title {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 700;
          color: #d63384;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          line-height: 1.25;
          white-space: normal;
          word-break: normal;
          overflow-wrap: break-word;
          text-align: center;
        }

        .lgs-card-title-short {
          font-size: 21px;
        }

        .lgs-card-title-medium {
          font-size: 18px;
        }

        .lgs-card-title-long {
          font-size: 16px;
        }

        .lgs-card-body {
          padding: 9px 15px 15px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .lgs-row {
          display: flex;
          align-items: center;
          gap: 3px;
          width: fit-content;
        }

        .lgs-row + .lgs-row { margin-top: 3px; }

        .lgs-qno {
          width: 33px;
          text-align: right;
          font-size: 16px;
          font-weight: 700;
          color: #999;
          flex-shrink: 0;
          padding-right: 4px;
        }

        .lgs-col-label {
          width: 42px;
          text-align: center;
          font-size: 15px;
          font-weight: 700;
          color: rgba(214,51,132,0.4);
          flex-shrink: 0;
        }

        .lgs-bubble {
          width: 38px;
          height: 38px;
          min-width: 38px;
          min-height: 38px;
          border-radius: 50%;
          border: 3px solid #d63384;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.1s;
          padding: 0;
          flex-shrink: 0;
          user-select: none;
          background: transparent;
          color: rgba(214,51,132,0.25);
        }

        .lgs-bubble[data-filled] {
          background: #d63384;
          color: #fff;
        }

        .lgs-bubble:hover {
          background: rgba(214,51,132,0.1);
          color: #d63384;
          transform: scale(1.06);
        }

        .lgs-bubble[data-filled]:hover {
          background: #c2185b;
          color: #fff;
        }

        @media (max-width: 768px) {
          .lgs-card {
            flex: none;
            width: 100% !important;
            max-width: none !important;
          }

          .lgs-card-head {
            height: auto;
            min-height: 84px;
            padding: 12px 18px;
          }

          .lgs-card-title {
            width: min(100%, 340px);
            font-size: 20px;
            line-height: 1.12;
            letter-spacing: 0.3px;
          }

          .lgs-card-title-short {
            font-size: 28px;
          }

          .lgs-card-title-medium {
            font-size: 23px;
          }

          .lgs-card-title-long {
            max-width: 300px;
            font-size: 20px;
            line-height: 1.12;
          }

          .lgs-card-body {
            width: 100%;
            padding: 15px 12px 18px;
            box-sizing: border-box;
          }

          .lgs-row {
            width: 100%;
            gap: clamp(4px, 1.4vw, 6px);
          }

          .lgs-row + .lgs-row {
            margin-top: 6px;
          }

          .lgs-qno {
            width: clamp(20px, 6vw, 30px);
            font-size: clamp(14px, 4.2vw, 21px);
            padding-right: 2px;
          }

          .lgs-bubble {
            flex: 1 1 0;
            width: auto;
            height: auto;
            min-width: 0;
            min-height: 0;
            max-width: none;
            aspect-ratio: 1 / 1;
            font-size: clamp(16px, 4.6vw, 23px);
            font-weight: 800;
            line-height: 1;
            border-width: 3px;
          }

          .lgs-branch {
            flex-direction: column;
            align-items: stretch;
          }

          .lgs-branch-actions {
            width: 100%;
          }

          .lgs-branch-btn {
            flex: 1;
          }
        }

        /* ─── Empty ─── */
        .lgs-empty {
          text-align: center;
          padding: 60px 24px;
        }

        .lgs-empty-ic {
          width: 56px;
          height: 56px;
          margin: 0 auto 14px;
          border-radius: 50%;
          background: #f0f0f2;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .lgs-empty-t {
          font-size: 15px;
          font-weight: 600;
          color: #999;
          margin-bottom: 6px;
        }

        .lgs-empty-d {
          font-size: 13px;
          color: #bbb;
        }

        /* ─── Finish ─── */
        .lgs-finish {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-top: auto;
          padding: 14px 20px;
          background: rgba(255, 255, 255, 0.42);
          border: 1.5px solid rgba(232, 232, 234, 0.72);
          border-radius: 14px;
          position: sticky;
          bottom: 0;
          z-index: 10;
          box-shadow: 0 10px 28px rgba(17, 24, 39, 0.08);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          margin-bottom: 2px;
        }

        .lgs-finish-actions {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .lgs-finish-txt {
          font-size: 13px;
          color: #999;
        }

        .lgs-finish-txt strong {
          color: #d63384;
          font-weight: 700;
        }

        .lgs-secondary-btn {
          padding: 10px 18px;
          border-radius: 10px;
          border: 1.5px solid #f3c2d8;
          background: #fff7fb;
          color: #c02d72;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.2s;
        }

        .lgs-secondary-btn:hover {
          background: #fff1f7;
          border-color: #e5a8c6;
        }

        .lgs-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(17, 24, 39, 0.42);
          backdrop-filter: blur(3px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          z-index: 1200;
        }

        .lgs-modal {
          width: min(100%, 460px);
          background: #fff;
          border: 1px solid #f1e4eb;
          border-radius: 22px;
          box-shadow: 0 24px 80px rgba(17, 24, 39, 0.22);
          padding: 24px;
        }

        .lgs-modal-title {
          font-size: 22px;
          font-weight: 800;
          color: #2a2a2a;
          margin-bottom: 6px;
        }

        .lgs-modal-desc {
          font-size: 14px;
          line-height: 1.5;
          color: #7a7a85;
          margin-bottom: 18px;
        }

        .lgs-modal-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          margin-bottom: 16px;
        }

        .lgs-modal-option {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 6px;
          padding: 16px 18px;
          border-radius: 16px;
          border: 1.5px solid #ebe8ef;
          background: #fff;
          text-align: left;
          cursor: pointer;
          transition: all 0.2s;
        }

        .lgs-modal-option[data-active] {
          border-color: #d63384;
          background: linear-gradient(180deg, #fff5fa 0%, #fff 100%);
          box-shadow: 0 10px 24px rgba(214, 51, 132, 0.12);
        }

        .lgs-modal-option-title {
          font-size: 15px;
          font-weight: 800;
          color: #2f3441;
        }

        .lgs-modal-option-text {
          font-size: 13px;
          line-height: 1.4;
          color: #7b8090;
        }

        .lgs-modal-field {
          margin-bottom: 14px;
        }

        .lgs-modal-field-title {
          font-size: 13px;
          font-weight: 800;
          color: #4b5563;
          margin-bottom: 8px;
        }

        .lgs-modal-toggle-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .lgs-modal-check {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 16px;
          border-radius: 16px;
          background: #faf7fb;
          border: 1px solid #efe7f1;
          margin-bottom: 18px;
          cursor: pointer;
        }

        .lgs-modal-check input {
          width: 16px;
          height: 16px;
          accent-color: #d63384;
        }

        .lgs-modal-check strong {
          display: block;
          font-size: 14px;
          color: #2f3441;
        }

        .lgs-modal-check span {
          display: block;
          font-size: 12px;
          color: #7b8090;
        }

        .lgs-modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        .lgs-modal-cancel,
        .lgs-modal-apply {
          border-radius: 12px;
          padding: 11px 18px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
        }

        .lgs-modal-cancel {
          background: #fff;
          border: 1.5px solid #e5e7eb;
          color: #6b7280;
        }

        .lgs-modal-apply {
          border: none;
          background: linear-gradient(135deg, #d63384, #b5246a);
          color: #fff;
          box-shadow: 0 8px 20px rgba(214,51,132,0.24);
        }

        .lgs-finish-btn {
          padding: 10px 28px;
          border: none;
          border-radius: 10px;
          background: linear-gradient(135deg, #d63384, #b5246a);
          color: #fff;
          font-size: 13px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 3px 12px rgba(214,51,132,0.25);
        }

        .lgs-finish-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 5px 20px rgba(214,51,132,0.35);
        }

        .lgs-finish-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }
      `}</style>

      <div className="lgs-root">

        <div className="lgs-topbar">
          <div className="lgs-sections">

            {hasTYT && (
              <div className="lgs-sec-wrap">
                <div className="lgs-sec" data-active={activeSection === 'tyt' || undefined}>
                  <button type="button" className="lgs-sec-main" onClick={() => handleSectionClick('tyt')}>
                    <span className="lgs-sec-label">TYT</span>
                    {tytP.filled > 0 && <span className="lgs-sec-prog">{tytP.filled}/{tytP.total}</span>}
                  </button>
                  <button type="button" className="lgs-sec-arrow" onClick={(e) => { e.stopPropagation(); setDdOpen(ddOpen === 'tyt' ? null : 'tyt'); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points={ddOpen === 'tyt' ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                    </svg>
                  </button>
                </div>
                <SubjectDropdown
                  subjects={tytSubjects} answers={answers}
                  selectedSubject={activeSection === 'tyt' ? selectedSubject : null}
                  onSelect={handleSubjectPick}
                  open={ddOpen === 'tyt'} onClose={() => setDdOpen(null)}
                />
              </div>
            )}

            {hasAYT && (
              <div className="lgs-sec-wrap">
                <div className="lgs-sec" data-active={activeSection === 'ayt' || undefined}>
                  <button type="button" className="lgs-sec-main" onClick={() => handleSectionClick('ayt')}>
                    <span className="lgs-sec-label">AYT</span>
                    {aytP.filled > 0 && <span className="lgs-sec-prog">{aytP.filled}/{aytP.total}</span>}
                  </button>
                  <button type="button" className="lgs-sec-arrow" onClick={(e) => { e.stopPropagation(); setDdOpen(ddOpen === 'ayt' ? null : 'ayt'); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points={ddOpen === 'ayt' ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                    </svg>
                  </button>
                </div>
                <SubjectDropdown
                  subjects={aytSubjects} answers={answers}
                  selectedSubject={activeSection === 'ayt' ? selectedSubject : null}
                  onSelect={handleSubjectPick}
                  open={ddOpen === 'ayt'} onClose={() => setDdOpen(null)}
                />
              </div>
            )}
          </div>

          {!hideBookletSelector && (
            <div className="lgs-kt">
              <span className="lgs-kt-label">Kitapçık</span>
              <button type="button" className="lgs-kt-btn" data-on={kitapcikTuru === 'A' || undefined}
                onClick={() => setKitapcikTuru('A')}>A</button>
              <button type="button" className="lgs-kt-btn" data-on={kitapcikTuru === 'B' || undefined}
                onClick={() => setKitapcikTuru('B')}>B</button>
            </div>
          )}
        </div>

        {activeSection && (
          <div className="lgs-tabs">
            <button type="button" className="lgs-tab" data-on={selectedSubject === null || undefined}
              onClick={() => setSelectedSubject(null)}>
              Tümü
            </button>
            {currentSubjects.map((sub) => {
              const f = answers[sub.key]?.filter(Boolean).length || 0;
              const dotAttr = f === 0 ? {} : f === sub.questionCount ? { 'data-done': true } : { 'data-part': true };
              return (
                <button key={sub.key} type="button" className="lgs-tab"
                  data-on={selectedSubject === sub.key || undefined}
                  onClick={() => setSelectedSubject(selectedSubject === sub.key ? null : sub.key)}>
                  <div className="lgs-tab-dot" {...dotAttr} />
                  {sub.shortLabel}
                  <span className="lgs-tab-count">{f}/{sub.questionCount}</span>
                </button>
              );
            })}
          </div>
        )}

        {activeSection ? (
          <>
            {subjectsToRender.some((sub) => sub.key === 'sosyal_bilimler' || sub.key === 'sosyal_bilimler2') && (
              <div className="lgs-branch">
                <span className="lgs-branch-title">
                  {subjectsToRender.some((sub) => sub.key === 'sosyal_bilimler2')
                    ? 'Sosyal Bilimler-2 seçimi'
                    : 'Sosyal Bilimler seçimi'}
                </span>
                <div className="lgs-branch-actions">
                  {(['din', 'felsefe'] as const).map((branch) => {
                    const targetKey = subjectsToRender.some((sub) => sub.key === 'sosyal_bilimler2') ? 'sosyal_bilimler2' : 'sosyal_bilimler';
                    return (
                      <button
                        key={branch}
                        type="button"
                        className="lgs-branch-btn"
                        data-on={branchSelections[targetKey] === branch || undefined}
                        onClick={() => setBranchSelections((prev) => ({ ...prev, [targetKey]: branch }))}
                      >
                        {branch === 'din' ? 'Din' : 'İlave Felsefe'}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="lgs-grid" data-count={subjectsToRender.length}>
              {subjectsToRender.map((sub) => (
                <SubjectGrid
                  key={sub.key} subject={sub} answers={answers[sub.key]}
                  onSelect={(qi, opt) => handleAnswer(sub.key, qi, opt)}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="lgs-empty">
            <div className="lgs-empty-ic">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><path d="M8 12l3 3 5-5" /></svg>
            </div>
            <div className="lgs-empty-t">Bölüm Seçin</div>
            <div className="lgs-empty-d">
              Yukarıdan
              {hasTYT && hasAYT ? <> <strong>TYT</strong> veya <strong>AYT</strong></> : hasTYT ? <> <strong>TYT</strong></> : <> <strong>AYT</strong></>}
              {' '}seçerek başlayın
            </div>
          </div>
        )}

        {isShuffleModalOpen && (
          <div className="lgs-modal-backdrop" onClick={() => setIsShuffleModalOpen(false)}>
            <div className="lgs-modal" onClick={(event) => event.stopPropagation()}>
              <div className="lgs-modal-title">Denemeyi Salla</div>
              <div className="lgs-modal-desc">Optiği otomatik doldurmak için bir yöntem seçin.</div>

              <div className="lgs-modal-grid">
                <button
                  type="button"
                  className="lgs-modal-option"
                  data-active={shuffleMode === 'mixed' || undefined}
                  onClick={() => setShuffleMode('mixed')}
                >
                  <span className="lgs-modal-option-title">Hepsi farklı</span>
                  <span className="lgs-modal-option-text">Tüm sorular rastgele farklı şıklarla işaretlensin.</span>
                </button>
                <button
                  type="button"
                  className="lgs-modal-option"
                  data-active={shuffleMode === 'single' || undefined}
                  onClick={() => setShuffleMode('single')}
                >
                  <span className="lgs-modal-option-title">Hepsi aynı şık</span>
                  <span className="lgs-modal-option-text">Her ders tek şıkla dolsun; dersler arasında A, B, C, D, E diye sırayla dönsün.</span>
                </button>
                <button
                  type="button"
                  className="lgs-modal-option"
                  data-active={shuffleMode === 'allCorrect' || undefined}
                  onClick={() => setShuffleMode('allCorrect')}
                >
                  <span className="lgs-modal-option-title">Hepsini doğru cevapla</span>
                  <span className="lgs-modal-option-text">Cevap anahtarındaki doğru şıklarla tüm optiği doldurur.</span>
                </button>
                <button
                  type="button"
                  className="lgs-modal-option"
                  data-active={shuffleMode === 'everyFourthWrong' || undefined}
                  onClick={() => setShuffleMode('everyFourthWrong')}
                >
                  <span className="lgs-modal-option-title">4 soruda 1 yanlış</span>
                  <span className="lgs-modal-option-text">Doğruları doldurur, her dördüncü soruda bilinçli bir yanlış yapar.</span>
                </button>
              </div>

              {!hideBookletSelector && (
                <div className="lgs-modal-field">
                  <div className="lgs-modal-field-title">Kitapçık</div>
                  <div className="lgs-modal-toggle-row">
                    <button
                      type="button"
                      className="lgs-kt-btn"
                      data-on={modalKitapcikTuru === 'A' || undefined}
                      onClick={() => setModalKitapcikTuru('A')}
                    >
                      A
                    </button>
                    <button
                      type="button"
                      className="lgs-kt-btn"
                      data-on={modalKitapcikTuru === 'B' || undefined}
                      onClick={() => setModalKitapcikTuru('B')}
                    >
                      B
                    </button>
                  </div>
                </div>
              )}

              {(['sosyal_bilimler', 'sosyal_bilimler2'] as const).map((targetKey) => {
                const hasSection = allSubjects.some((sub) => sub.key === targetKey);
                if (!hasSection) return null;

                return (
                  <div key={targetKey} className="lgs-modal-field">
                    <div className="lgs-modal-field-title">
                      {targetKey === 'sosyal_bilimler' ? 'TYT Sosyal Seçimi' : 'AYT Sosyal-2 Seçimi'}
                    </div>
                    <div className="lgs-modal-toggle-row">
                      <button
                        type="button"
                        className="lgs-branch-btn"
                        data-on={modalBranchSelections[targetKey] === 'din' || undefined}
                        onClick={() => setModalBranchSelections((prev) => ({ ...prev, [targetKey]: 'din' }))}
                      >
                        Din
                      </button>
                      <button
                        type="button"
                        className="lgs-branch-btn"
                        data-on={modalBranchSelections[targetKey] === 'felsefe' || undefined}
                        onClick={() => setModalBranchSelections((prev) => ({ ...prev, [targetKey]: 'felsefe' }))}
                      >
                        İlave Felsefe
                      </button>
                    </div>
                  </div>
                );
              })}

              <label className="lgs-modal-check">
                <input
                  type="checkbox"
                  checked={leaveBlankEveryFourth}
                  disabled={shuffleMode === 'allCorrect' || shuffleMode === 'everyFourthWrong'}
                  onChange={(event) => setLeaveBlankEveryFourth(event.target.checked)}
                />
                <div>
                  <strong>4 soruda 1 boşluk bırak</strong>
                  <span>Her dördüncü soruyu boş bırakarak daha gerçekçi dağılım oluşturur.</span>
                </div>
              </label>

              <div className="lgs-modal-actions">
                <button type="button" className="lgs-modal-cancel" onClick={() => setIsShuffleModalOpen(false)}>
                  Vazgeç
                </button>
                <button type="button" className="lgs-modal-apply" onClick={handleShuffleApply}>
                  Uygula
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="lgs-finish">
          <span className="lgs-finish-txt">Toplam: <strong>{filledAll}</strong> / {totalAll} soru</span>
          <div className="lgs-finish-actions">
            {/* <button type="button" className="lgs-secondary-btn" onClick={handleOpenShuffleModal} disabled={submitting}>
              Denemeyi Salla
            </button> */}
            <button type="button" className="lgs-finish-btn" onClick={handleFinish} disabled={!canSubmit || submitting}>
              {submitting ? 'Gönderiliyor...' : submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
